import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ScreenshotLightbox } from "../components/ScreenshotLightbox";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Plus,
  Lifebuoy,
  ChatCircle,
  Clock,
  CheckCircle,
  Warning,
  Image as ImageIcon,
  X,
  Upload,
  Paperclip,
} from "phosphor-react";
import SupportHelpCenter from "../components/SupportHelpCenter";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyContent, setReplyContent] = useState("");
  const [formData, setFormData] = useState({
    subject: "",
    category: "general",
    priority: "normal",
    description: "",
  });
  
  // Screenshot state - ONLY store serializable data, never File objects
  const [screenshot, setScreenshot] = useState(null); // {preview, uploading, uploaded, data, error, filename}
  const [isDragging, setIsDragging] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const uploadAbortRef = useRef(null);
  const pendingFileRef = useRef(null); // Store file reference separately (not in state)

  useEffect(() => {
    fetchTickets();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cancel any in-flight upload
      if (uploadAbortRef.current) {
        uploadAbortRef.current.abort();
      }
      // Revoke preview URL
      if (screenshot?.preview) {
        URL.revokeObjectURL(screenshot.preview);
      }
    };
  }, []);

  // Clipboard paste handler for screenshots
  useEffect(() => {
    if (!isDialogOpen) return;
    
    const handlePaste = async (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            await handleScreenshotSelect(file);
          }
          break;
        }
      }
    };
    
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [isDialogOpen]);

  const handleScreenshotSelect = async (file) => {
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please use PNG, JPG, or WebP');
      return;
    }
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }
    
    // Cancel any previous upload
    if (uploadAbortRef.current) {
      uploadAbortRef.current.abort();
    }
    
    // Store file in ref (NOT in state)
    pendingFileRef.current = file;
    
    // Create preview - store only serializable data in state
    const preview = URL.createObjectURL(file);
    setScreenshot({ 
      preview, 
      filename: file.name,
      uploading: true, 
      uploaded: false, 
      data: null, 
      error: null 
    });
    
    // Use XMLHttpRequest instead of fetch to bypass platform's fetch interceptor
    // that causes "postMessage: Request object could not be cloned" errors
    const xhr = new XMLHttpRequest();
    uploadAbortRef.current = { abort: () => xhr.abort() };
    
    const timeoutId = setTimeout(() => {
      xhr.abort();
      setScreenshot(prev => prev ? { ...prev, uploading: false, uploaded: false, error: 'Upload timed out' } : null);
      toast.error('Upload timed out. Please try again.');
    }, 30000);
    
    xhr.open('POST', `${API}/support/upload-screenshot`, true);
    xhr.withCredentials = true;
    
    xhr.onload = () => {
      clearTimeout(timeoutId);
      uploadAbortRef.current = null;
      
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          setScreenshot(prev => prev ? { 
            ...prev, 
            uploading: false, 
            uploaded: true, 
            data, 
            error: null 
          } : null);
          toast.success('Screenshot uploaded');
        } catch (e) {
          setScreenshot(prev => prev ? { ...prev, uploading: false, uploaded: false, error: 'Invalid response' } : null);
          toast.error('Upload failed: Invalid response');
        }
      } else {
        let errorMsg = 'Upload failed';
        try {
          const err = JSON.parse(xhr.responseText);
          errorMsg = err.detail || errorMsg;
        } catch (e) {
          // JSON parse failed
        }
        setScreenshot(prev => prev ? { ...prev, uploading: false, uploaded: false, error: errorMsg } : null);
        toast.error(errorMsg);
      }
    };
    
    xhr.onerror = () => {
      clearTimeout(timeoutId);
      uploadAbortRef.current = null;
      setScreenshot(prev => prev ? { ...prev, uploading: false, uploaded: false, error: 'Network error' } : null);
      toast.error('Upload failed: Network error');
    };
    
    xhr.onabort = () => {
      clearTimeout(timeoutId);
      uploadAbortRef.current = null;
      // Only show error if we haven't already (timeout case handles its own message)
    };
    
    const fd = new FormData();
    fd.append('file', file);
    xhr.send(fd);
  };

  const retryUpload = () => {
    // Use the file from ref, not from state
    if (pendingFileRef.current) {
      handleScreenshotSelect(pendingFileRef.current);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      await handleScreenshotSelect(files[0]);
    }
  };

  const handleFileInputChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await handleScreenshotSelect(file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const removeScreenshot = () => {
    if (screenshot?.preview) {
      URL.revokeObjectURL(screenshot.preview);
    }
    pendingFileRef.current = null;
    setScreenshot(null);
  };

  const resetForm = () => {
    setFormData({
      subject: "",
      category: "general",
      priority: "normal",
      description: "",
    });
    removeScreenshot();
  };

  const fetchTickets = async () => {
    try {
      const response = await fetch(`${API}/support/tickets`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets);
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Don't submit while screenshot is uploading
    if (screenshot?.uploading) {
      toast.error('Please wait for screenshot upload to complete');
      return;
    }
    
    try {
      // Include screenshot data if uploaded
      const ticketData = { ...formData };
      if (screenshot?.uploaded && screenshot?.data) {
        Object.assign(ticketData, screenshot.data);
      }
      
      const response = await fetch(`${API}/support/tickets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(ticketData),
      });

      if (response.ok) {
        toast.success("Support ticket created");
        setIsDialogOpen(false);
        resetForm();
        fetchTickets();
      } else {
        toast.error("Failed to create ticket");
      }
    } catch (error) {
      toast.error("Failed to create ticket");
    }
  };

  const submitReply = async () => {
    if (!selectedTicket || !replyContent.trim()) return;
    try {
      const response = await fetch(`${API}/support/tickets/${selectedTicket.ticket_id}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: replyContent }),
      });

      if (response.ok) {
        toast.success("Reply sent");
        setReplyContent("");
        fetchTickets();
        // Refresh selected ticket
        const updatedTicket = tickets.find(t => t.ticket_id === selectedTicket.ticket_id);
        if (updatedTicket) {
          setSelectedTicket({ ...updatedTicket });
        }
      }
    } catch (error) {
      toast.error("Failed to send reply");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: "bg-blue-50 text-blue-700 border-blue-300",
      in_progress: "bg-amber-50 text-amber-700 border-amber-300",
      waiting_on_customer: "bg-purple-50 text-purple-700 border-purple-300",
      resolved: "bg-emerald-50 text-emerald-700 border-emerald-300",
      closed: "bg-zinc-100 text-zinc-700 border-zinc-300",
    };
    return colors[status] || colors.open;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "resolved":
      case "closed":
        return <CheckCircle size={16} />;
      case "in_progress":
        return <Clock size={16} />;
      default:
        return <ChatCircle size={16} />;
    }
  };

  return (
    <div className="space-y-6" data-testid="support-page">
      {/* Help Center - Prepended above tickets */}
      <SupportHelpCenter onCreateTicket={() => setIsDialogOpen(true)} />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-semibold uppercase">
          Support Tickets
        </h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase text-sm"
              data-testid="new-ticket-btn"
            >
              <Plus size={18} className="mr-2" />
              New Ticket
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg" data-testid="create-ticket-modal">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl uppercase">
                Create Support Ticket
              </DialogTitle>
            </DialogHeader>
            <DialogBody>
              <form id="create-ticket-form" onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Subject *</Label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="Brief description of the issue"
                    className="mt-1 h-12 border-2"
                    required
                    data-testid="ticket-subject-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Category</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(v) => setFormData({ ...formData, category: v })}
                    >
                      <SelectTrigger className="mt-1 h-12 border-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="billing">Billing</SelectItem>
                        <SelectItem value="technical">Technical</SelectItem>
                        <SelectItem value="feature">Feature Request</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select
                      value={formData.priority}
                      onValueChange={(v) => setFormData({ ...formData, priority: v })}
                    >
                      <SelectTrigger className="mt-1 h-12 border-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Description *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Please describe your issue in detail..."
                    className="mt-1 border-2"
                    rows={4}
                    required
                    data-testid="ticket-description-input"
                  />
                </div>
                
                {/* Screenshot Upload Section */}
                <div>
                  <Label className="text-zinc-600">Attach a screenshot (optional)</Label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileInputChange}
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    className="hidden"
                  />
                  
                  {!screenshot ? (
                    <div
                      ref={dropZoneRef}
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`mt-1 p-4 border-2 border-dashed rounded-sm cursor-pointer transition-colors ${
                        isDragging 
                          ? 'border-[#FF6600] bg-[#FF6600]/5' 
                          : 'border-zinc-300 hover:border-zinc-400'
                      }`}
                      data-testid="screenshot-dropzone"
                    >
                      <div className="flex flex-col items-center text-center">
                        <Upload size={24} className="text-zinc-400 mb-2" />
                        <p className="text-sm text-zinc-600">Drop image here</p>
                        <p className="text-xs text-zinc-400 mt-1">
                          or paste (Ctrl+V / Cmd+V) or click to upload
                        </p>
                        <p className="text-xs text-zinc-400 mt-1">
                          PNG, JPG, WebP • Max 1 image • 10MB
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className={`mt-1 p-3 border-2 rounded-sm ${screenshot.error ? 'border-red-300 bg-red-50' : 'border-zinc-200 bg-zinc-50'}`}>
                      <div className="flex items-start gap-3">
                        <div className="relative flex-shrink-0">
                          <img
                            src={screenshot.uploaded && screenshot.data?.thumb_url 
                              ? `${API.replace('/api', '')}${screenshot.data.thumb_url}` 
                              : screenshot.preview}
                            alt="Screenshot preview"
                            className="w-20 h-20 object-cover rounded border border-zinc-200"
                          />
                          {screenshot.uploading && (
                            <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded">
                              <div className="w-5 h-5 border-2 border-[#FF6600] border-t-transparent rounded-full animate-spin" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-zinc-700 truncate">
                            {screenshot.filename || 'Screenshot'}
                          </p>
                          <p className="text-xs mt-0.5">
                            {screenshot.uploaded ? (
                              <span className="text-emerald-600">✓ Uploaded</span>
                            ) : screenshot.error ? (
                              <span className="text-red-600">✗ {screenshot.error}</span>
                            ) : screenshot.uploading ? (
                              <span className="text-zinc-500">Uploading...</span>
                            ) : (
                              <span className="text-zinc-500">Ready</span>
                            )}
                            {screenshot.data?.screenshot_size_bytes && (
                              <span className="ml-2 text-zinc-500">
                                {(screenshot.data.screenshot_size_bytes / 1024).toFixed(0)} KB
                              </span>
                            )}
                          </p>
                          <div className="flex gap-2 mt-2">
                            {screenshot.error && (
                              <button
                                type="button"
                                onClick={retryUpload}
                                className="text-xs text-[#FF6600] hover:text-[#CC5200] underline"
                              >
                                Retry
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              className="text-xs text-zinc-500 hover:text-zinc-700 underline"
                            >
                              Replace
                            </button>
                            <button
                              type="button"
                              onClick={removeScreenshot}
                              className="text-xs text-red-500 hover:text-red-700 underline"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </form>
            </DialogBody>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => { setIsDialogOpen(false); resetForm(); }}
                className="flex-1 border-2"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="create-ticket-form"
                className="flex-1 bg-[#FF6600] hover:bg-[#CC5200] text-white"
                data-testid="submit-ticket-btn"
                disabled={screenshot?.uploading}
              >
                {screenshot?.uploading ? 'Uploading...' : 'Submit Ticket'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Ticket Detail Modal - Gold Standard Styling */}
      <Dialog open={!!selectedTicket} onOpenChange={(open) => !open && setSelectedTicket(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto p-0">
          {/* Modal Header */}
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-200">
            <DialogTitle className="font-heading text-lg uppercase tracking-tight">
              {selectedTicket?.subject}
            </DialogTitle>
          </DialogHeader>
          
          {selectedTicket && (
            <div className="p-6 space-y-5">
              {/* Status and Category Badges */}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={getStatusColor(selectedTicket.status)}>
                  {getStatusIcon(selectedTicket.status)}
                  <span className="ml-1.5 capitalize">{selectedTicket.status.replace("_", " ")}</span>
                </Badge>
                <Badge variant="secondary" className="bg-zinc-100 text-zinc-700">
                  {selectedTicket.category}
                </Badge>
              </div>
              
              {/* Original Ticket Description */}
              <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-lg">
                <p className="text-sm whitespace-pre-wrap leading-relaxed text-zinc-700">
                  {selectedTicket.description}
                </p>
                <p className="text-xs text-zinc-400 mt-3 pt-3 border-t border-zinc-200">
                  Created {new Date(selectedTicket.created_at).toLocaleString()}
                </p>
              </div>
              
              {/* Screenshot Display */}
              {(selectedTicket.screenshot?.full_url || selectedTicket.screenshot_url) && (
                <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-lg">
                  <p className="text-xs font-medium text-zinc-500 mb-3 flex items-center gap-1.5">
                    <Paperclip size={14} />
                    Attached Screenshot
                  </p>
                  <button
                    type="button"
                    onClick={() => setLightboxOpen(true)}
                    className="block text-left"
                  >
                    <img
                      src={`${API.replace('/api', '')}${selectedTicket.screenshot?.thumb_url || selectedTicket.screenshot_url}`}
                      alt="Screenshot"
                      loading="lazy"
                      className="max-w-full max-h-48 rounded-lg border border-zinc-200 hover:opacity-90 transition-opacity cursor-pointer"
                    />
                  </button>
                  <p className="text-xs text-zinc-400 mt-2">
                    Click to view full size
                    {(selectedTicket.screenshot?.filename || selectedTicket.screenshot_filename) && (
                      <span className="ml-2">• {selectedTicket.screenshot?.filename || selectedTicket.screenshot_filename}</span>
                    )}
                  </p>
                </div>
              )}

              {/* Replies Thread */}
              {selectedTicket.replies?.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide">
                    Replies ({selectedTicket.replies.length})
                  </p>
                  {selectedTicket.replies.map((reply, idx) => (
                    <div
                      key={reply.reply_id || idx}
                      className={`p-4 border rounded-lg ${
                        reply.is_admin
                          ? "bg-blue-50 border-blue-200"
                          : "bg-white border-zinc-200"
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap leading-relaxed text-zinc-700">
                        {reply.content}
                      </p>
                      <p className="text-xs text-zinc-400 mt-3 pt-3 border-t border-zinc-100">
                        {reply.user_name} • {new Date(reply.created_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Reply Form */}
              {selectedTicket.status !== "closed" && (
                <div className="space-y-3 pt-3 border-t border-zinc-200">
                  <Textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="Write a reply..."
                    className="border-2 border-zinc-200 focus:border-[#FF6600] min-h-[100px] resize-none"
                    rows={4}
                    data-testid="reply-textarea"
                  />
                  <div className="flex justify-end">
                    <Button
                      onClick={submitReply}
                      disabled={!replyContent.trim()}
                      className="bg-[#FF6600] hover:bg-[#CC5200] text-white px-6"
                      data-testid="send-reply-btn"
                    >
                      Send Reply
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tickets List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-20 bg-zinc-200 rounded-sm animate-pulse" />
          ))}
        </div>
      ) : tickets.filter(t => t.status !== "closed").length > 0 ? (
        <div className="space-y-4">
          {tickets.filter(t => t.status !== "closed").map((ticket) => (
            <Card
              key={ticket.ticket_id}
              className="border-2 border-zinc-200 card-hover cursor-pointer"
              onClick={() => setSelectedTicket(ticket)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#FF6600]/10 border border-[#FF6600]/30 flex items-center justify-center flex-shrink-0">
                      <Lifebuoy size={20} className="text-[#FF6600]" />
                    </div>
                    <div>
                      <p className="font-medium">{ticket.subject}</p>
                      <p className="text-sm text-zinc-500 line-clamp-1">
                        {ticket.description}
                      </p>
                      <p className="text-xs text-zinc-400 mt-1">
                        {new Date(ticket.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge variant="outline" className={getStatusColor(ticket.status)}>
                      {ticket.status.replace("_", " ")}
                    </Badge>
                    <div className="flex items-center gap-2 text-xs text-zinc-400">
                      {ticket.screenshot_url && (
                        <span className="flex items-center gap-1 text-zinc-500" title="Has screenshot">
                          <Paperclip size={12} />
                          Screenshot
                        </span>
                      )}
                      {ticket.replies?.length > 0 && (
                        <span>
                          {ticket.replies.length} replies
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-2 border-dashed border-zinc-300">
          <CardContent className="p-12 text-center">
            <Lifebuoy size={48} className="mx-auto mb-4 text-zinc-300" />
            <h3 className="font-heading text-lg font-semibold uppercase mb-2">
              No Support Tickets
            </h3>
            <p className="text-zinc-500 mb-6">
              Need help? Create a support ticket and we&apos;ll get back to you.
            </p>
            <Button
              onClick={() => setIsDialogOpen(true)}
              className="bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase text-sm"
            >
              <Plus size={18} className="mr-2" />
              New Ticket
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Screenshot Lightbox - use full_url for lightbox, supports both formats */}
      {(selectedTicket?.screenshot?.full_url || selectedTicket?.screenshot_url) && (
        <ScreenshotLightbox
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          imageUrl={`${API.replace('/api', '')}${selectedTicket.screenshot?.full_url || selectedTicket.screenshot_url}`}
          filename={selectedTicket.screenshot?.filename || selectedTicket.screenshot_filename}
        />
      )}
    </div>
  );
}
